import React from "react";
//#region types
import { MonthSummaryProps } from "models/propTypes/timeReportingPropTypes";
import { IconDefinitionConstants } from "utils/constants";
import { TranslationKeys } from "translations";
import { TimeReportingEnums } from "models/enums";
import { TimeReportingFormTypes } from "models/formTypes";
//#endregion
//#region components
import { Divider, Grid } from "@mui/material";
import { Icon, ViewPanel } from "components/common";
import { useSalaryPermissions } from "components/salary";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance, UnitsHelper } from "utils/helpers";
import TimeReportHelperInstance from "components/timeReporting/timeReportHelper";

interface ProjectSummary {
  rows: TimeReportingFormTypes.MonthlyTimeReportRow[];
  name: string;
  id?: string;
}

interface WeekSummary {
  week: string;
  weekIndex: number;
  catagorySummeries: string[];
}

const { formatHours, roundHours } = TimeReportHelperInstance;

const MonthSummary: React.FC<MonthSummaryProps> = (props) => {
  const { formData, monthWeeks, allowanceSummary } = props;
  // Rounded once, so a noisy sum like 1e-15 reads as "matches full time" rather than "+0 h".
  const compHours = props.compHours !== undefined ? roundHours(props.compHours) : undefined;

  const [projectTotal, setProjectTotal] = React.useState<number>(0);
  const [deviationTotal, setDeviationTotal] = React.useState<number>(0);
  const { isSalaryDeviationsAvailable } = useSalaryPermissions();

  const projectSummary = React.useMemo(() => {
    let allProjectTotal = 0;
    const groupedProjects: ProjectSummary[] = [];

    formData
      ?.filter((row) => row.type === TimeReportingEnums.TimeReportType.project)
      .forEach((row) => {
        let project = groupedProjects.find((proj) => proj.id === row.projectId);

        if (!project) {
          project = { id: row.projectId, name: row.name, rows: [] };
          groupedProjects.push(project);
        }

        project.rows.push(row);
      });

    const projectData = groupedProjects.map((proj) => {
      let projectTotal = 0;
      const categoriesTotal: string[] = [];
      let weekSummaries: WeekSummary[] = [];

      proj.rows.forEach((row) => {
        let categoryHours = 0;

        monthWeeks?.forEach((week, index) => {
          const hours = week.displayText ? Number((row as any)[week.displayText]) : 0;

          if (hours > 0) {
            categoryHours += hours;
            const weekText = `${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.WeekPascal)} ${
              week.weekNumber
            }`;

            let weekSummary = weekSummaries.find((ws) => ws.week === weekText);

            if (!weekSummary) {
              weekSummary = { week: weekText, weekIndex: index, catagorySummeries: [] };
              weekSummaries.push(weekSummary);
            }

            weekSummary.catagorySummeries.push(
              `${UnitsHelper.getTimeWithHoursSuffix(formatHours(hours))} ${row.categoryName}`
            );
          }
        });

        if (categoryHours > 0) {
          projectTotal += categoryHours;
          categoriesTotal.push(`${UnitsHelper.getTimeWithHoursSuffix(formatHours(categoryHours))} ${row.categoryName}`);
        }
      });

      allProjectTotal += projectTotal;
      weekSummaries = weekSummaries.sort((a, b) => a.weekIndex - b.weekIndex);
      return { name: proj.name, total: projectTotal, categoriesTotal: categoriesTotal, weeks: weekSummaries };
    });

    setProjectTotal(allProjectTotal);
    return projectData;
  }, [formData]);

  const deviationSummary = React.useMemo(() => {
    let allDeviationTotal = 0;
    const deviationRows = formData?.filter((row) => row.type === TimeReportingEnums.TimeReportType.deviation);

    const deviationData = monthWeeks
      ?.map((week, index) => {
        const deviationWeek = week.weekNumber;
        return deviationRows
          ?.map((dr) => {
            const hours = week.displayText ? Number((dr as any)[week.displayText]) : 0;
            if (hours > 0) {
              allDeviationTotal += hours;
              return `${LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.Common.WeekPascal
              )} ${deviationWeek}, ${LocalizationHelperInstance.getFormattedMessage(
                `SalaryDeviation.Type_${dr.deviationType}`
              )} (${dr.childName ? dr.childName + `, ` : ""}${formatHours(hours)} ${LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.Common.Hours
              )})`;
            } else {
              return undefined;
            }
          })
          .filter((dr) => dr !== undefined);
      })
      .flatMap((d) => d);
    setDeviationTotal(allDeviationTotal);
    return deviationData;
  }, [formData]);

  const getTotal = () => formatHours(projectTotal + deviationTotal);

  const getDisplayValue = (value: number) => {
    const displayValue = formatHours(value);
    const displayValueWithSign = value > 0 ? `+${displayValue}` : displayValue;
    return UnitsHelper.getTimeWithHoursSuffix(displayValueWithSign);
  };

  return (
    <Grid className={cssExports.summaryContainer}>
      <ViewPanel>
        <Grid container direction={"column"} spacing={2}>
          <Grid item container>
            <Grid className={cssExports.viewPanelIcon}>
              <Icon icon={IconDefinitionConstants.WTimer} />
            </Grid>
            <span>{`${getTotal()} ${LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Common.Hours
            )}`}</span>
            <span>
              {compHours !== undefined &&
                (compHours === 0
                  ? ` (${LocalizationHelperInstance.getFormattedMessage(
                      TranslationKeys.TimeReporting.CompTimeMatchesFullTime
                    )})`
                  : ` (${getDisplayValue(compHours)} ${LocalizationHelperInstance.getFormattedMessage(
                      TranslationKeys.TimeReporting.CompHoursComparedToFullTime
                    )})`)}
            </span>
          </Grid>

          <Grid item>
            <Divider className={cssExports.dividerGray} />
          </Grid>
          {projectSummary && (
            <Grid item container spacing={2}>
              <Grid item container alignItems={"center"}>
                <Grid item className={cssExports.viewPanelIcon}>
                  <Icon icon={IconDefinitionConstants.WFolder} />
                </Grid>
                <Grid item>{LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.Project)}</Grid>
              </Grid>
              {projectSummary?.length ? (
                <Grid item container>
                  <Grid spacing={2} container direction={"column"}>
                    {projectSummary.map((proj) => (
                      <Grid container spacing={1} item key={`project-${proj.name}`}>
                        <Grid item container direction={"column"}>
                          <Grid item xs>
                            {`${proj.name} (${formatHours(proj.total)} ${LocalizationHelperInstance.getFormattedMessage(
                              TranslationKeys.Common.Hours
                            )})`}
                          </Grid>
                          <Grid item className={cssExports.summaryGroupContent}>
                            {`${proj.categoriesTotal.join(", ")}`}
                          </Grid>
                        </Grid>
                        <Grid item container>
                          {proj.weeks?.map((weekSummary) => (
                            <Grid item container key={`project-day-${weekSummary.week}`}>
                              <Grid item xs={3}>
                                {weekSummary.week}
                              </Grid>
                              <Grid item className={cssExports.summaryGroupContent}>
                                {weekSummary.catagorySummeries.join(", ")}
                              </Grid>
                            </Grid>
                          ))}
                        </Grid>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              ) : (
                <Grid item container className={cssExports.summaryGroupPlaceholder}>
                  {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.ProjectPlaceholder)}
                </Grid>
              )}
            </Grid>
          )}
          {isSalaryDeviationsAvailable && (
            <Grid item>
              <Divider className={cssExports.dividerGray} />
            </Grid>
          )}
          {isSalaryDeviationsAvailable && deviationSummary && (
            <Grid item container spacing={2}>
              <Grid item container>
                <Grid item className={cssExports.viewPanelIcon}>
                  <Icon icon={IconDefinitionConstants.WCalendarTime} />
                </Grid>
                <Grid item>{LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Deviations)} </Grid>
              </Grid>

              {deviationSummary?.length ? (
                <Grid item>
                  {deviationSummary.map((dev, index) => (
                    <Grid item xs key={`deviation-${index}`}>
                      {dev}
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Grid item container className={cssExports.summaryGroupPlaceholder}>
                  {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.DeviationsPlaceholder)}
                </Grid>
              )}
            </Grid>
          )}
          {isSalaryDeviationsAvailable && allowanceSummary && (
            <>
              <Grid item>
                <Divider className={cssExports.dividerGray} />
              </Grid>
              <Grid item container spacing={2}>
                <Grid item container>
                  <Grid item className={cssExports.viewPanelIcon}>
                    <Icon icon={IconDefinitionConstants.WMoney} />
                  </Grid>
                  <Grid item>
                    {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.Allowance)}{" "}
                  </Grid>
                </Grid>

                {allowanceSummary?.length ? (
                  <Grid item>
                    {allowanceSummary.map((allowance, index) => (
                      <Grid item xs key={`allowance-${index}`}>
                        {allowance}
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Grid item container className={cssExports.summaryGroupPlaceholder}>
                    {LocalizationHelperInstance.getFormattedMessage(
                      TranslationKeys.TimeReporting.AllowancesPlaceholder
                    )}
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </Grid>
      </ViewPanel>
    </Grid>
  );
};

export default MonthSummary;
